import React, { useState, useEffect } from 'react';
import api, { fileUrl } from '@/api';
import { MagnifyingGlass, Star, Plus, X, ArrowSquareOut, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { SECTORS } from '@/utils/sector_options';
import StartupBadges, { STARTUP_BADGES } from '../../components/StartupBadges';

const STAGES = ['', 'Pre-Seed', 'Seed', 'Pre-Series A', 'Series A', 'Series B'];
const PAGE_SIZE = 50;

const TrackerListModal = ({ startupName, lists, onSelect, onCreate, onClose }) => {
  const [newName, setNewName] = useState('');
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="tracker-list-modal">
      <div className="bg-surface border border-border rounded-xl p-5 w-[360px] max-w-[90vw]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-text-primary">Add {startupName} to Tracker</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary"><X size={18} /></button>
        </div>
        <div className="space-y-1.5 mb-4 max-h-[200px] overflow-y-auto">
          <button onClick={() => onSelect(null)} className="w-full text-left px-3 py-2.5 text-sm rounded-lg border border-border text-text-secondary hover:bg-background hover:text-text-primary transition-colors" data-testid="select-list-uncategorized">
            General Watchlist
          </button>
          {lists.map(l => (
            <button key={l.id} onClick={() => onSelect(l.id)} className="w-full text-left px-3 py-2.5 text-sm rounded-lg border border-border text-text-secondary hover:bg-background hover:text-text-primary transition-colors" data-testid={`select-list-${l.id}`}>
              {l.name} <span className="text-text-muted text-xs">({l.count})</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="New list name..." onKeyDown={e => { if (e.key === 'Enter' && newName.trim()) onCreate(newName.trim()); }}
            className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-primary" data-testid="new-list-input" />
          <button onClick={() => { if (newName.trim()) onCreate(newName.trim()); }} disabled={!newName.trim()} className="px-3 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50" data-testid="create-list-btn">
            <Plus size={16} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
};

const VCDiscover = ({ user }) => {
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterBadge, setFilterBadge] = useState('');
  const [trackingStatus, setTrackingStatus] = useState({});
  const [trackerLists, setTrackerLists] = useState([]);
  const [modalTarget, setModalTarget] = useState(null); // {id, name}
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchData();
  }, []);

  // Reset to the first page whenever the filtered set changes underneath us.
  useEffect(() => {
    setPage(1);
  }, [search, filterSector, filterStage, filterBadge]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [startupsRes, statusRes, listsRes] = await Promise.all([
        api.get('/vc/discover'),
        api.get('/vc/tracker/status/batch'),
        api.get('/vc/tracker/lists'),
      ]);
      setStartups(startupsRes.data);
      setTrackingStatus(statusRes.data);
      setTrackerLists(listsRes.data.lists || []);
    } catch (error) {
      console.error('Failed to fetch', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToTracker = async (startupId, listId) => {
    try {
      await api.post('/vc/tracker/add', { startup_id: startupId, list_id: listId });
      toast.success('Added to tracker!');
      setTrackingStatus(prev => ({ ...prev, [startupId]: { tracking: true, list_id: listId } }));
      setModalTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleCreateListAndAdd = async (name) => {
    try {
      const res = await api.post('/vc/tracker/lists', { name });
      const newList = res.data;
      setTrackerLists(prev => [...prev, { ...newList, count: 0 }]);
      if (modalTarget) handleAddToTracker(modalTarget.id, newList.id);
    } catch (err) { toast.error('Failed to create list'); }
  };

  // Client-side filtering
  const filtered = startups.filter(s => {
    if (search && !s.startup_name?.toLowerCase().includes(search.toLowerCase()) && !s.one_line_description?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterSector && s.sector !== filterSector) return false;
    if (filterStage && s.funding_stage !== filterStage) return false;
    if (filterBadge && !s.badges?.includes(filterBadge)) return false;
    return true;
  });

  // Client-side pagination: 50 per page over the filtered set.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paged = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary mb-2" data-testid="discover-heading">Discover Startups</h1>
        <p className="text-text-secondary text-[13px] mb-6">Find and track promising startups across sectors and stages.</p>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-6" data-testid="discover-filters">
          <div className="flex items-center gap-2 border border-border rounded-lg px-3 py-2.5 w-full sm:flex-1 sm:min-w-[250px] sm:max-w-[400px]">
            <MagnifyingGlass size={16} className="text-text-muted" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or description..." className="bg-transparent text-sm text-text-primary placeholder-text-muted focus:outline-none flex-1" data-testid="discover-search" />
            {search && <button onClick={() => setSearch('')} className="text-text-muted hover:text-text-primary"><X size={14} /></button>}
          </div>
          <select value={filterSector} onChange={e => setFilterSector(e.target.value)} className="w-full sm:w-auto bg-transparent border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none" data-testid="discover-filter-sector">
            <option value="">All Sectors</option>
            {SECTORS.map((sector) => (<option key={sector} value={sector}>{sector}</option>))}
          </select>
          <select value={filterStage} onChange={e => setFilterStage(e.target.value)} className="w-full sm:w-auto bg-transparent border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none" data-testid="discover-filter-stage">
            <option value="">All Stages</option>
            {STAGES.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterBadge} onChange={e => setFilterBadge(e.target.value)} className="w-full sm:w-auto bg-transparent border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none" data-testid="discover-filter-badge">
            <option value="">All Badges</option>
            {STARTUP_BADGES.map(badge => <option key={badge.id} value={badge.id}>{badge.label}</option>)}
          </select>
          <span className="text-xs text-text-muted sm:ml-auto">
            {filtered.length} startups
            {filtered.length > PAGE_SIZE && ` · showing ${pageStart + 1}–${Math.min(pageStart + PAGE_SIZE, filtered.length)}`}
          </span>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-16 text-text-secondary">Loading startups...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-text-muted">No startups match your filters</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="discover-grid">
            {paged.map(startup => {
              const isTracking = trackingStatus[startup.id]?.tracking;
              return (
                <div key={startup.id} className="bg-surface border border-border rounded-[10px] p-4 sm:p-5 hover:border-primary/30 transition-all flex flex-col" data-testid={`discover-${startup.id}`}>
                  <div className="flex items-start gap-3 mb-3">
                    {startup.logo_url ? (
                      <img src={fileUrl(startup.logo_url)} className="w-11 h-11 rounded-lg object-cover flex-shrink-0" alt="" />
                    ) : (
                      <div className="w-11 h-11 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-primary font-mono font-bold text-sm">{startup.startup_name?.substring(0, 2).toUpperCase()}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-bold text-text-primary truncate">{startup.startup_name}</h3>
                        {startup.website && (
                          <a href={startup.website.startsWith('http') ? startup.website : `https://${startup.website}`} target="_blank" rel="noopener noreferrer" className="text-text-muted hover:text-primary">
                            <ArrowSquareOut size={13} />
                          </a>
                        )}
                      </div>
                      <div className="flex gap-1.5 flex-wrap mt-1">
                        {startup.sector && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{startup.sector}</span>}
                        {startup.funding_stage && <span className="text-[10px] px-2 py-0.5 rounded-full bg-info/10 text-info font-medium">{startup.funding_stage}</span>}
                        {startup.hq_city && <span className="text-[10px] px-2 py-0.5 rounded-full bg-surface border border-border text-text-muted">{startup.hq_city}</span>}
                      </div>
                      <StartupBadges badges={startup.badges} size="xs" className="mt-2" />
                    </div>
                  </div>

                  <p className="text-sm text-text-secondary mb-3 line-clamp-2 min-h-[40px]">{startup.one_line_description || ''}</p>

                  <div className="grid grid-cols-3 gap-3 mb-4 text-xs">
                    <div><span className="text-text-muted">MRR Range</span><div className="font-medium text-text-primary">{startup.mrr_range || 'N/A'}</div></div>
                    <div><span className="text-text-muted">Team Size</span><div className="font-medium text-text-primary">{startup.team_size || 'N/A'}</div></div>
                    <div><span className="text-text-muted">Location</span><div className="font-medium text-text-primary">{startup.hq_city || 'N/A'}</div></div>
                  </div>

                  <div className="mt-auto">
                    {isTracking ? (
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-primary/10 text-primary rounded-lg text-sm font-medium" data-testid={`tracking-${startup.id}`}>
                        <Star size={16} weight="fill" /> Tracking
                      </div>
                    ) : (
                      <button
                        onClick={() => setModalTarget({ id: startup.id, name: startup.startup_name })}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                        data-testid={`add-tracker-${startup.id}`}
                      >
                        <Plus size={16} weight="bold" /> Add to Tracker
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-8" data-testid="discover-pagination">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="flex items-center gap-1 border border-border rounded-lg px-3 py-2 text-sm text-text-secondary hover:bg-background hover:text-text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-text-secondary"
              data-testid="discover-page-prev"
            >
              <CaretLeft size={14} weight="bold" /> Prev
            </button>
            <span className="text-sm text-text-muted" data-testid="discover-page-indicator">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="flex items-center gap-1 border border-border rounded-lg px-3 py-2 text-sm text-text-secondary hover:bg-background hover:text-text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-text-secondary"
              data-testid="discover-page-next"
            >
              Next <CaretRight size={14} weight="bold" />
            </button>
          </div>
        )}
      </div>

      {/* Tracker List Selection Modal */}
      {modalTarget && (
        <TrackerListModal
          startupName={modalTarget.name}
          lists={trackerLists}
          onSelect={(listId) => handleAddToTracker(modalTarget.id, listId)}
          onCreate={handleCreateListAndAdd}
          onClose={() => setModalTarget(null)}
        />
      )}
    </div>
  );
};

export default VCDiscover;
